import {
  api,
  BUNDLE_SECTIONS_SUGGESTIONS_CATEGORY_LIST_TAG,
  BUNDLE_SECTIONS_SUGGESTIONS_SUB_CATEGORY_LIST_TAG,
  BUNDLE_TAG,
  refreshBundleSectionsWithCurrentFilters,
  useLazyPostRedoBundleQuery,
  useLazyPostUndoBundleQuery,
} from '@/api';
import { BROADCAST_UNDO_REDO_BUNDLE, useFletchersBroadcast } from '@/broadcast';
import {
  BundleType,
  clearPageInfoCache,
  popBusyTask,
  pushBusyTask,
  selectBundleUndoRedoHistory,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  store,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { Button, RedoIcon, UndoIcon } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect } from 'react';

export const BundleUndoRedoButtons = () => {
  const dispatch = useAppDispatch();
  const { sendMessageWithoutResponse } = useFletchersBroadcast();

  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundleUndoRedoHistory = useAppSelector(selectBundleUndoRedoHistory);

  const [triggerPostUndoBundle] = useLazyPostUndoBundleQuery();
  const [triggerPostRedoBundle] = useLazyPostRedoBundleQuery();

  useEffect(() => {
    /**
     * Broadcast listener
     */
    const controller = new AbortController();
    const signal = controller.signal;

    const updateDataAfterUndoRedo = (event: CustomEvent) => {
      if (signal.aborted) {
        console.info(`Listener Aborted: ${BROADCAST_UNDO_REDO_BUNDLE}`);
        return;
      }

      if (
        (event.detail.payload.isUndo || event.detail.payload.isRedo) &&
        activeBundleType !== event.detail.payload.broadcastBundleType
      ) {
        refreshBundleSectionsWithCurrentFilters(dispatch, store.getState());
        dispatch(
          api.util.invalidateTags([
            BUNDLE_TAG,
            BUNDLE_SECTIONS_SUGGESTIONS_CATEGORY_LIST_TAG,
            BUNDLE_SECTIONS_SUGGESTIONS_SUB_CATEGORY_LIST_TAG,
          ]),
        );
      }
    };

    window.addEventListener(BROADCAST_UNDO_REDO_BUNDLE, updateDataAfterUndoRedo as EventListener, {
      signal,
    });

    return () => {
      controller.abort();
    };
  }, [activeBundleType, dispatch]);

  const handleUndoClick = useCallback(() => {
    if (!bundleId || (activeBundleType !== BundleType.FULL && activeBundleType !== BundleType.LIGHT)) return;
    const busyTaskId = `undo-bundle-${Date.now()}`;
    dispatch(pushBusyTask({ id: busyTaskId, description: 'Undoing last change...' }));
    triggerPostUndoBundle({ bundleId: bundleId })
      .unwrap()
      .then(() => {
        // Clear page info cache to reset inLightBundle status on thumbnails
        dispatch(clearPageInfoCache({ key: activeBundleType }));
        sendMessageWithoutResponse(BROADCAST_UNDO_REDO_BUNDLE, {
          isUndo: true,
          broadcastBundleType: activeBundleType,
        });
      })
      .finally(() => {
        dispatch(popBusyTask(busyTaskId));
      });
  }, [bundleId, activeBundleType, triggerPostUndoBundle, sendMessageWithoutResponse, dispatch]);

  const handleRedoClick = useCallback(() => {
    if (!bundleId || (activeBundleType !== BundleType.FULL && activeBundleType !== BundleType.LIGHT)) return;
    const busyTaskId = `redo-bundle-${Date.now()}`;
    dispatch(pushBusyTask({ id: busyTaskId, description: 'Redoing change...' }));
    triggerPostRedoBundle({ bundleId: bundleId })
      .unwrap()
      .then(() => {
        // Clear page info cache to reset inLightBundle status on thumbnails
        dispatch(clearPageInfoCache({ key: activeBundleType }));
        sendMessageWithoutResponse(BROADCAST_UNDO_REDO_BUNDLE, {
          isRedo: true,
          broadcastBundleType: activeBundleType,
        });
      })
      .finally(() => {
        dispatch(popBusyTask(busyTaskId));
      });
  }, [bundleId, activeBundleType, triggerPostRedoBundle, sendMessageWithoutResponse, dispatch]);

  return (
    <div
      className={classNames('border-fletchers-gray-110 flex h-full max-h-[32px] overflow-hidden rounded-[4px] border')}
    >
      <Button
        icon={<UndoIcon className="h-[14px] w-[14px] -translate-y-px text-black" />}
        onClick={handleUndoClick}
        outlined
        className="!border-r-fletchers-gray-110 !h-[32px] !w-[32px] rounded-none! !border-0 !border-r !border-solid !px-[9px] !py-[9px] hover:!bg-gray-50"
        disabled={!bundleUndoRedoHistory || bundleUndoRedoHistory.applied === 0}
      />
      <Button
        icon={<RedoIcon className="h-[14px] w-[14px] -translate-y-px text-black" />}
        onClick={handleRedoClick}
        outlined
        className="!h-[32px] !w-[32px] rounded-none! !border-0 !px-[9px] !py-[9px] hover:!bg-gray-50"
        disabled={!bundleUndoRedoHistory || bundleUndoRedoHistory.undone === 0}
      />
    </div>
  );
};
